import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Key, Settings, LogOut, ChevronDown } from 'lucide-react';
import { API_BASE_URL, clearAuthToken } from '../../lib/api';

interface ProfileMenuProps {
  user: {
    name: string;
    email: string;
    avatar?: string;
    role: string;
  };
}

export function ProfileMenu({ user }: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, { 
        method: 'POST',
        credentials: 'include',
      });
      // トークンをクリア
      clearAuthToken();
      // ログインページにリダイレクト
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout failed:', err);
      clearAuthToken();
      // エラーでもログインページにリダイレクト
      window.location.href = '/login';
    }
  };

  const getRoleBadge = (role: string) => {
    const badges: Record<string, { label: string; color: string }> = {
      admin: { label: '管理者', color: 'bg-purple-100 text-purple-700' },
      user: { label: 'ユーザー', color: 'bg-blue-100 text-blue-700' },
      viewer: { label: '閲覧者', color: 'bg-gray-100 text-gray-700' },
    };
    return badges[role] || badges.user;
  };

  const badge = getRoleBadge(user.role);

  return (
    <div className="relative" ref={menuRef}>
      {/* プロフィールボタン */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg transition-colors"
        style={{ 
          backgroundColor: isOpen ? 'var(--light-gray-primary)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--light-gray-primary)';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {user.avatar ? (
          <img 
            src={user.avatar} 
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            {user.name.charAt(0).toUpperCase()}
          </div>
        )}
        <ChevronDown 
          size={16} 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
        />
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg py-2 z-50"
          style={{
            backgroundColor: 'var(--background-card)',
            border: '1px solid var(--border-color)',
          }}
        >
          {/* ユーザー情報 */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-3">
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                  style={{ backgroundColor: 'var(--accent-color)' }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                  {user.name}
                </p>
                <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
                  {user.email}
                </p>
              </div>
            </div>
            <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full ${badge.color}`}>
              {badge.label}
            </span>
          </div>

          {/* メニュー項目 */}
          <div className="py-1">
            <Link
              to="/profile"
              className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => setIsOpen(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--light-gray-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <User size={18} style={{ color: 'var(--text-secondary)' }} />
              プロフィール変更
            </Link>
            <Link
              to="/change-password"
              className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => setIsOpen(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--light-gray-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Key size={18} style={{ color: 'var(--text-secondary)' }} />
              パスワード変更
            </Link>
            <Link
              to="/settings"
              className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => setIsOpen(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--light-gray-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Settings size={18} style={{ color: 'var(--text-secondary)' }} />
              設定
            </Link>
          </div>

          {/* ログアウト */}
          <div className="border-t py-1" style={{ borderColor: 'var(--border-color)' }}>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-sm w-full text-left text-red-600 transition-colors"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--light-gray-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <LogOut size={18} />
              ログアウト
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
